import config from "@/config";
import Axios from "axios";
import { isLoggedIn, refreshToken } from "@/login";
import { collectPaged } from "./utils";
import type { Frame, UploadFrame, Frames } from "@/types/frames";
import type {
  StickerCollection,
  Sticker,
  StickerCreateRequest,
} from "./types/stickers";
import type {
  MailOutOrder,
  MailOutOrderListApiResponse,
  MailOutOrderDetail,
  MailOutOrderListRequest,
  MailOutOrderListResponse,
} from "@/types/mailout";
import type {
  ApiTagTree,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from "@/types/tagManagement";
import type {
  PriceManagementProductPricing,
  PriceManagementUpdatePriceListRequest,
  PriceManagementUpdatePriceListResponse,
  PriceManagementUpdateVatRequest,
  PriceManagementUpdateVatResponse,
  PriceManagementUpdateSapPriceRequest,
  PriceManagementUpdateSapPriceResponse,
  PriceManagementCloneResponse,
  PriceManagementDeleteResponse,
} from "@/types/priceManagement";
import type {
  BacksideFormat,
  PdfPreviewUploadUrlResponse,
} from "@/types/backsideTemplate";

export type SystemLogEntry = {
  _id: string;
  user: string;
  logDate: string;
  topic: string;
  message: string;
  data: Record<string, string>;
};

export type PromotionCodeLogEntry = {
  _id: string;
  createdAt: string;
  code: string;
  createdBy: string;
  data: Record<string, string>;
};

export type SubscriptionHistoryEntry = {
  status?: string;
  date?: string;
};

export type NewsletterContact = {
  email: string;
  subscriptionStatus: string;
  lastUpdated: string;
  userId?: string;
  history?: SubscriptionHistoryEntry[];
};
export type NewsletterStatus = {
  contact: NewsletterContact;
};

export type Level = "info" | "critical";
export type PaymentInfo = {
  order: Order;
  postcard: Postcard[];
  history: boolean;
};
export type NopsOrder = {
  paymentId: string;
  orderId: string;
  paymentStatus: string;
  itemCount: number;
  paymentMethod: string;
  registrationDate: string;
  verificationDate: string;
  finalizedDate: string;
  totalAmount: number;
  countryCode: string;
  finalized: boolean;
  platform: string;
  currencyCode: string;
};
export type NopsInfo = {
  order: NopsOrder;
};
export type NotificationMessage = {
  title: string;
  message: string;
  topic?: string;
  token?: string;
};
export type Platform = "web" | "android" | "ios";

export type Title = {
  en: string;
  sv?: string;
  da?: string;
  nb?: string;
};

export type View = "start" | "cart" | "checkout";

export type DateRule = {
  from: string | null;
  to: string | null;
};

export type VersionRule = {
  from: string | null;
  to: string | null;
};

export type Rules = {
  date?: DateRule;
  version?: VersionRule;
  view?: View[];
};

export type Messages = {
  items: Message[];
};

export type Message = {
  id?: string;
  created?: string;
  platform: Platform;
  level: Level;
  title: Title;
  rules: Rules;
};

export type PlatformMessages = {
  critical: Message[];
  info: Message[];
};

export type ImageDescription = {
  language: string;
  description: string;
};

export type UploadImage = {
  attribution: string;
  attributionType: string;
  hidden: boolean;
  languages: string[];
  categories: number[];
  filename: string;
  base64: string;
  imageDescription: ImageDescription[];
};

export type Image = {
  _id: string;
  code: string;
  attribution: string;
  attributionType: string;
  hidden: boolean;
  languages: string[];
  categories: number[];
  sortOrder: number;
  imageUrl: string;
  previewUrl: string;
  thumbUrl: string;
  imageDescription: ImageDescription[];
};

export type Images = {
  images: Image[];
};

export type UploadCategory = {
  filename: string;
  base64: string;
  startDate?: string | undefined;
  endDate?: string | undefined;
  hidden: boolean;
  swedish?: string | undefined;
  english?: string | undefined;
  danish?: string | undefined;
  norwegian?: string | undefined;
};

export type ImageCategory = {
  categoryId: number;
  createdAt?: Date;
  createdBy?: string;
  sortOrder: number;
  iconUrl: string;
  startDate?: string | undefined;
  endDate?: string | undefined;
  hidden: boolean;
  swedish?: string | undefined;
  english?: string | undefined;
  danish?: string | undefined;
  norwegian?: string | undefined;
};

export type ImageCategories = {
  categories: ImageCategory[];
};

export type Receiver = {
  timeElapsed?: number;
  tries?: number;
  error?: boolean;
  batchId: string;
  status: string;
  trackingId: string;
  name: string;
  name2: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  countryCode: string;
  resendCheck: boolean;
  _id: string;
};

export type PdfFrontside = {
  tries: number;
  generated: boolean;
  error: boolean;
};
export type PostcardContent = {
  frontImageUrl: string;
  backImageUrl: string;
};
export type Postcard = {
  orderId: string;
  processedCount: number;
  sentCount: number;
  cardId: string;
  key: string;
  cartId: string;
  createdDate: string;
  appId: string;
  deviceId: string;
  format: string;
  attribution: string;
  userId: string;
  countryCode: string;
  cardContent: PostcardContent;
  customerReference: string;
  receivers: Receiver[];
  frontside: PdfFrontside;
  batchIds: string[];
  dlqFailure: string;
  expiresAt: string;
  devProps: string[];
  postcardIndex: number;
  _id: string;
};

export type OrderStatus =
  | "registered"
  | "paid"
  | "finalized"
  | "published"
  | "printed"
  | "cancelled"
  | "declined";

export type CountryCode = "SE" | "DK" | "NO" | "GLOBAL";

export type Country = { code: CountryCode; name: string };

export const countries: Record<CountryCode, Country> = {
  SE: { code: "SE", name: "Sweden" },
  DK: { code: "DK", name: "Denmark" },
  NO: { code: "NO", name: "Norway" },
  GLOBAL: { code: "GLOBAL", name: "International" },
};

export type OrderLineItem = {
  itemId: string;
  format: string;
  customerReference: string;
};

export type OrderHistoryEntry = {
  status: string;
  date: string;
  declineExplanation: string;
  fromCallback: boolean;
  fromCronjob: boolean;
};

export type OrderSubtotalItem = {
  format: string;
  countryCode: string;
  cardId: string;
  quantity: number;
  totalPrice: number;
};

export type OrderSubtotalDiscount = {
  type: string;
  amount: number;
};

export type OrderSubtotal = {
  items: OrderSubtotalItem[];
  discounts?: OrderSubtotalDiscount[];
  totalGrossPrice: number;
  totalNetPrice: number;
};

// migrate users to users, convert prop names on the way
// lowercase all emails
// just delete and do images all over
// lowercase emails on order? sounds like a lot of work

export type Order = {
  orderId: string;
  cartId: string;
  customerReference: string;
  orderStatus: OrderStatus;
  orderDate: string;
  lineItems: OrderLineItem[];
  totalAmount: number;
  currencyCode: string;
  paymentMethod: string;
  platform: string;
  declineExplanation: string;
  receiptNumber: string;
  customerEmail: string;
  promotionCode?: string;
  deviceId: string;
  appId: string;
  userId?: string;
  totalReceivers: number;
  freeRecivers: number;
  freeReciversPromotion: number;
  freeReceiversBalance: number;
  history: OrderHistoryEntry[];
  expiresAt: string;
  subtotal: OrderSubtotal;
  language: string;
  countryCode: string;
};

export type User = {
  userId: string;
  email: string;
  createdAt: string;
  lastLoginAt?: string;
  signedUpForNewsletter: boolean;
  saveReceivers: boolean;
  legacyBalance?: number;
  consumedPromotionCodes?: {
    promotionCode: string;
    consumedAt: string;
    isLegacyCode: boolean;
  }[];
};

export type UserReceiver = {
  hash: number;
  name: string;
  name2?: string;
  address: string;
  postalCode: string;
  city: string;
  countryCode: string;
  createdAt: string;
  lastUsed: string;
};

export type UserReceivers = {
  userId: string;
  receivers: UserReceiver[];
  expiresAt: string;
};

export type PromotionCodeBatch = {
  title: string;
  codes: PromotionCode[];
};
export type ActionLog = {
  deviceId?: string;
  userId?: string;
  logDate: string;
  level: string;
  action: string;
  message?: string;
  cardId?: string;
  cartId?: string;
  orderId?: string;
  imageCode?: string;
  promotionCode?: string;
  imageUrl?: string;
};
export type Device = {
  deviceId: string;
  supportRef: string;
  createdAt: string;
  lastRegistration: string;
  legacyBalance: number;
  platform: string;
  userId?: string;
  location?: string;
  language?: string;
  appVersion?: string;
  deviceOsVersion?: string;
  deviceDescription?: string;
};
export type Application = {
  appId: string;
  deviceId: string;
  email: string;
  customerNr: string;
  balance: number;
};
export type LegacyDevice = {
  appId: string;
  deviceId: string;
  customerNo: string;
  registrationDate: string;
  email: string;
  newsletter: boolean;
  lastRegisterDeviceDate: string;
  appVersion: string;
  languageIsoCode: string;
  region: string;
  deviceDescription: string;
  appStore: string;
  appName: string;
  deviceVersion: string;
  deviceType: string;
  deviceBalance: string;
};
export type PromotionCode = {
  type: string;
  value: number;
  amount: number;
  id: string;
  code: string;
  expireAt?: string;
  createdAt: string;
  createdBy: string;
  campaign?: string;
  usageLimit?: number;
  cardFormats: string[];
  requireLogin: boolean;
};

export type Document = {
  FileId: number;
  DocumentId: string;
  ExtDocumentId: string;
  InvoiceNumber: string;
  LastChange: string;
  Channels: DocumentChannel[];
};

export type DocumentChannel = {
  ChannelId: number;
  ChannelName: string;
  Status: string;
  Documents: number;
  Pages: number;
  DeliveryTime: string;
};
export type Stats = {
  status: string;
  result: { date: Date; count: number }[];
};
export type ReceiptData = {
  html: string;
  text: string;
  subject: string;
};
export type Role = {
  email: string;
  role: string;
  lastLogin: string;
  active: boolean;
};
export type PriceObject = Record<"SE" | "GLOBAL", number>;

export type PriceList = ReadonlyArray<{
  format: string;
  currency: string;
  prices: PriceObject;
}>;

class RvApi {
  headers = async () => {
    if (!isLoggedIn() && !(await refreshToken())) {
      throw "loginerror";
    }
    return {
      Authorization: "Bearer " + localStorage.getItem("pn-access-token"),
      "Content-Type": "application/json; charset=UTF-8",
    };
  };
  uri = (href: string) => {
    return `${config.apiHost}/${href}`;
  };

  raw = async (uri: string) => {
    const result = await Axios.get(uri, {
      headers: { "Content-Type": "application/json; charset=UTF-8" },
    });
    return result.data;
  };
  urlExists = async (url: string): Promise<boolean> => {
    const result = await Axios.head(url);
    return result.status === 200;
  };

  downloadImage = async (uri: string) => {
    const mime = uri.endsWith(".png") ? "image/png" : "image/jpeg";
    const result = await Axios.get(uri, {
      headers: {
        Accept: mime,
      },
      responseType: "arraybuffer",
    });
    return result.data;
  };

  getStacktrace = async (input: object): Promise<string> => {
    const result = await Axios.post<string>(
      this.uri("admin/stacktrace"),
      input,
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };

  getReceiptContent = async (orderId: string): Promise<ReceiptData> => {
    const result = await Axios.get(this.uri(`admin/receipt/${orderId}`), {
      headers: await this.headers(),
    });
    return result.data;
  };

  getAllRoles = async (): Promise<Role[]> => {
    const r = await Axios.get<{ roles: Role[] }>(this.uri("admin/role/all"), {
      headers: await this.headers(),
    });
    return r.data?.roles;
  };
  setRole = async (email: string, role: string, active: boolean) => {
    const r = await Axios.put<{ result: string }>(
      this.uri("admin/role"),
      { email, role, active },
      {
        headers: await this.headers(),
      },
    );
    return r.data;
  };

  getImagePresignedUrl = async (
    filename: string,
    type: string,
    mime: string,
  ): Promise<{ presignedUrl: string; imageUrl: string }> => {
    const body = {
      contentType: mime,
      type,
      name: filename,
    };
    const r = await Axios.post<any>(
      this.uri("admin/images/createuploadurl"),
      body,
      { headers: await this.headers() },
    );
    return r.data;
  };
  searchGenericLookup = async <T>(
    type: string,
    q: string,
    l: number,
    o: number,
  ): Promise<T[]> => {
    const result = await Axios.get<{ result: [] }>(
      this.uri(`admin/search/generic?type=${type}&q=${q}&o=${o}&l=${l}`),
      { headers: await this.headers() },
    );
    return result.data.result;
  };
  recentOrders = async (
    id: string,
    type: string,
    length: number,
    offset: number,
  ): Promise<Order[]> => {
    const result = await Axios.get<{ orders: Order[] }>(
      this.uri(
        `admin/search/recentorders?id=${id}&type=${type}&l=${length}&o=${offset}`,
      ),
      {
        headers: await this.headers(),
      },
    );
    return result.data.orders;
  };
  searchOrderId = async (q: string): Promise<Order> => {
    const result = await Axios.get<{ order: Order }>(
      this.uri(`admin/search/orderid?q=${q}`),
      {
        headers: await this.headers(),
      },
    );
    return result.data.order;
  };
  searchSupportRef = async (q: string): Promise<Device[]> => {
    const result = await Axios.get<{ devices: Device[] }>(
      this.uri(`admin/search/supportref?q=${q}`),
      {
        headers: await this.headers(),
      },
    );
    return result.data.devices;
  };
  searchLegacy = async (
    type: string,
    q: string[],
    l?: number,
  ): Promise<any[]> => {
    const qstr = q.join("&q=");
    const searcher = async (skip: number, limit: number) => {
      const result = await Axios.get(
        this.uri(
          `admin/search/legacy?type=${type}&q=${qstr}&l=${limit}&o=${skip}`,
        ),
        {
          headers: await this.headers(),
        },
      );
      return result?.data?.result;
    };
    return collectPaged(searcher, l ?? 50);
  };
  searchLegacyCustomer = async (q: string): Promise<LegacyDevice[]> => {
    const result = await Axios.get(
      this.uri(`admin/search/legacycustomer?q=${q}`),
      {
        headers: await this.headers(),
      },
    );
    return result.data.devices;
  };
  searchLegacyDevice = async (q: string): Promise<LegacyDevice[]> => {
    const result = await Axios.get(
      this.uri(`admin/search/legacydevice?q=${q}`),
      {
        headers: await this.headers(),
      },
    );
    return result.data.devices;
  };
  searchLegacyFreetext = async (
    q: string,
  ): Promise<{ devices: Device[]; legacy: LegacyDevice[] }> => {
    const result = await Axios.get(
      this.uri(`admin/search/legacyfreetext?q=${q}`),
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };

  searchFreetext = async (
    q: string,
  ): Promise<{ users: User[]; orders: Order[] }> => {
    const result = await Axios.get(this.uri(`admin/search/freetext?q=${q}`), {
      headers: await this.headers(),
    });
    return result.data;
  };

  searchBatchId = async (q: string): Promise<{ orders: Order[] }> => {
    const result = await Axios.get(this.uri(`admin/search/batchid?q=${q}`), {
      headers: await this.headers(),
    });
    return result.data;
  };
  searchUser = async (q: string): Promise<User[]> => {
    const result = await Axios.get<{ users: User[] }>(
      this.uri(`admin/search/users?q=${q}`),
      { headers: await this.headers() },
    );
    return result.data.users;
  };
  searchPromotionCode = async (q: string): Promise<PromotionCode[]> => {
    const result = await Axios.get<{ codes: PromotionCode[] }>(
      this.uri(`admin/search/promotioncodes?q=${q}`),
      { headers: await this.headers() },
    );
    return result.data.codes;
  };

  searchDevices = async (id: string): Promise<Device[]> => {
    const result = await Axios.get(this.uri(`admin/search/devices?q=${id}`), {
      headers: await this.headers(),
    });
    return result.data.devices;
  };

  searchActionLog = async (
    id: string,
    type: string,
    length: number,
    offset: number,
    debug: boolean,
  ): Promise<ActionLog[]> => {
    const level = debug ? "debug" : "info";
    const result = await Axios.get(
      this.uri(
        `admin/search/logs?id=${id}&type=${type}&l=${length}&o=${offset}&level=${level}`,
      ),
      {
        headers: await this.headers(),
      },
    );
    return result.data.logs;
  };

  writeActionLog = async (
    message: string,
    deviceId: string,
    userId?: string,
    orderId?: string,
  ): Promise<ActionLog> => {
    const payload = {
      action: "note",
      userId,
      clientDeviceId: deviceId,
      message,
      orderId,
    };

    const result = await Axios.post(this.uri("admin/log"), payload, {
      headers: await this.headers(),
    });
    return result.data;
  };

  getPricing = async (): Promise<PriceList> => {
    const result = await Axios.get(this.uri("v2/pricing?currency=SEK"), {
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
      },
    });
    return result.data;
  };
  getUser = async (id: string): Promise<User> => {
    const result = await Axios.get(this.uri(`admin/users/${id}`), {
      headers: await this.headers(),
    });
    return result.data;
  };
  getUserReceivers = async (id: string): Promise<UserReceivers> => {
    const result = await Axios.get(
      this.uri(`admin/search/userreceivers?q=${id}`),
      {
        headers: await this.headers(),
      },
    );
    return result.data.userReceivers;
  };
  unsubscribeEmail = async (email: string): Promise<void> => {
    await Axios.post(
      this.uri(`admin/newsletter/unsubscribe`),
      { email },
      {
        headers: await this.headers(),
      },
    );
  };
  exportNewsletter = async (date: string): Promise<string> => {
    const result = await Axios.get(
      this.uri(`admin/newsletterexport?date=${date}`),
      {
        headers: await this.headers(),
      },
    );
    return result.data.downloadUrl;
  };
  getNewsletterInfo = async (
    email: string,
  ): Promise<NewsletterStatus | null> => {
    const result = await Axios.post(
      this.uri(`admin/newsletter/status`),
      { email },
      {
        headers: await this.headers(),
      },
    );
    if (result.status !== 200) {
      return null;
    }
    return result.data;
  };
  unsubscribeUser = async (id: string): Promise<void> => {
    await Axios.get(this.uri(`admin/users/${id}/unsubscribe-from-newsletter`), {
      headers: await this.headers(),
    });
  };
  dataExportLink = async (id: string): Promise<string> => {
    const result = await Axios.get(this.uri(`admin/dataexport/${id}`), {
      headers: await this.headers(),
    });
    return result.data.downloadUrl;
  };

  getPaymentInfo = async (id: string): Promise<PaymentInfo> => {
    const result = await Axios.get(
      this.uri(`admin/search/orderdetails?q=${id}`),
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };

  migrateBalanceToUser = async (
    userId: string,
    fromDevice: string,
    legacy?: boolean,
  ) => {
    const result = await Axios.post(
      this.uri(`admin/users/${userId}/migrate-balance`),
      { fromDevice, legacy },
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };

  resendCards = async (id: string, indexes: number[]) => {
    await Axios.put(this.uri(`admin/resend/cards/${id}`), indexes, {
      headers: await this.headers(),
    });
  };

  resendReceipt = async (orderId: string, email: string): Promise<string> => {
    try {
      const result = await Axios.put<{ ok: boolean }>(
        this.uri(`admin/resend/receipt/${orderId}`),
        { email },
        {
          headers: await this.headers(),
        },
      );
      if (result.data.ok) {
        return "ok";
      } else {
        return "Receipt resend failed";
      }
    } catch (e: any) {
      if (e.name !== "AxiosError") {
        return "Receipt resend failed";
      }
      if (
        e?.response?.status == 400 &&
        e?.response?.data?.errorCode === "INPUT_VALIDATION_ERROR" &&
        e?.response?.data?.errorMessage === '"email" is not allowed to be empty'
      ) {
        return "Must enter an email address";
      }
      if (
        e?.response?.status == 400 &&
        e?.response?.data?.errorCode === "INPUT_VALIDATION_ERROR"
      ) {
        return `"${email}" is not a valid email address`;
      }
      return "Receipt resend failed";
    }
  };

  createCampaign = async (
    code: string,
    value: number,
    usageLimit: number,
    expireAt: string,
    type?: string,
    requireLogin?: boolean,
  ): Promise<PromotionCode> => {
    const data = {
      code: code,
      type: type ?? "free_card",
      requireLogin: requireLogin ?? true,
      campaign: code,
      value: value,
      usageLimit: usageLimit,
      expireAt: expireAt,
      cardFormats: ["A5", "A51", "A6"],
    };

    const result = await Axios.post(this.uri("promotioncodes"), data, {
      headers: await this.headers(),
    });
    return result.data;
  };

  createGoodwill = async (
    code: string,
    value: number,
    expireAt: string,
  ): Promise<PromotionCode> => {
    const data = {
      code: code,
      type: "free_card",
      requireLogin: false,
      value: value,
      usageLimit: 1,
      expireAt: expireAt,
      cardFormats: ["A5", "A51", "A6", "A4"],
    };

    const result = await Axios.post(this.uri("promotioncodes"), data, {
      headers: await this.headers(),
    });
    return result.data;
  };

  createUnique = async (
    codeCount: number,
    value: number,
    expireAt: string,
    campaign: string | undefined,
  ): Promise<PromotionCodeBatch> => {
    const data = {
      amount: codeCount,
      cardFormats: ["A5", "A51", "A6", "A4"],
      createdBy: "",
      type: "free_card",
      value: value,
      campaign,
      expireAt: expireAt,
    };
    const result = await Axios.post(this.uri("promotioncodes/batch"), data, {
      headers: await this.headers(),
    });
    return result.data;
  };

  getFrames = async (): Promise<Frames> => {
    const result = await Axios.get(this.uri("admin/images/frames"), {
      headers: await this.headers(),
    });
    return result.data;
  };
  getFrame = async (id: string): Promise<Frame> => {
    const result = await Axios.get(this.uri(`admin/images/frames/${id}`), {
      headers: await this.headers(),
    });
    return result.data;
  };
  getFramesByGroupId = async (groupId: string): Promise<Frames> => {
    const result = await Axios.get(
      this.uri(`admin/images/framesbygroups/${groupId}`),
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };
  saveFrame = async (id: string, data: UploadFrame): Promise<Frame> => {
    if (id === "") {
      id = "new";
    }
    const result = await Axios.put(
      this.uri(`admin/images/frames/${id}`),
      data,
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };

  deleteFrame = async (id: number): Promise<void> => {
    const result = await Axios.delete(this.uri(`admin/images/frames/${id}`), {
      headers: await this.headers(),
    });
    return result.data;
  };

  deleteCollectionImage = async (id: string): Promise<void> => {
    const result = await Axios.delete(this.uri(`admin/images/${id}`), {
      headers: await this.headers(),
    });
    return result.data;
  };

  reorderFrames = async (data: number[]): Promise<void> => {
    await Axios.put(
      this.uri(`admin/images/frames`),
      { frames: data },
      {
        headers: await this.headers(),
      },
    );
  };

  getImageCategories = async (): Promise<ImageCategories> => {
    const result = await Axios.get(this.uri("admin/images/categories"), {
      headers: await this.headers(),
    });
    return result.data;
  };
  getImageCategory = async (id: string): Promise<ImageCategory> => {
    const result = await Axios.get(this.uri(`admin/images/categories/${id}`), {
      headers: await this.headers(),
    });
    return result.data;
  };
  saveImageCategory = async (
    id: string | undefined,
    data: UploadCategory,
  ): Promise<ImageCategory> => {
    if (!id) {
      id = "new";
    }
    const result = await Axios.put(
      this.uri(`admin/images/categories/${id}`),
      data,
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };
  deleteImageCategory = async (id: number): Promise<void> => {
    const result = await Axios.delete(
      this.uri(`admin/images/categories/${id}`),
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };
  reorderImageCategories = async (data: number[]): Promise<void> => {
    await Axios.put(
      this.uri(`admin/images/categories`),
      { categories: data },
      {
        headers: await this.headers(),
      },
    );
  };

  getImages = async (id: number): Promise<Images> => {
    const result = await Axios.get(this.uri(`admin/images?category=${id}`), {
      headers: await this.headers(),
    });
    return result.data;
  };
  getImage = async (id: string): Promise<Image> => {
    const result = await Axios.get(this.uri(`admin/images/${id}`), {
      headers: await this.headers(),
    });
    return result.data;
  };
  saveImage = async (
    id: string | undefined,
    data: UploadImage,
  ): Promise<Image> => {
    if (!id) {
      id = "new";
    }
    const result = await Axios.put(this.uri(`admin/images/${id}`), data, {
      headers: await this.headers(),
    });
    return result.data;
  };
  deleteImage = async (id: string): Promise<void> => {
    const result = await Axios.delete(this.uri(`admin/images/${id}`), {
      headers: await this.headers(),
    });
    return result.data;
  };
  reorderImages = async (data: string[]): Promise<void> => {
    await Axios.put(
      this.uri(`admin/images`),
      { images: data },
      {
        headers: await this.headers(),
      },
    );
  };

  getCampaignCodes = async (): Promise<PromotionCodeBatch> => {
    const result = await Axios.get(
      this.uri("promotioncodes/listcampaigncodes"),
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };
  updateSystemStatusMessage = async (
    message: Message,
    id: string,
  ): Promise<void> => {
    console.log(message);
    await Axios.put(
      this.uri(`systemstatus/messages/${message.platform}/${id}`),
      message,
      {
        headers: await this.headers(),
      },
    );
  };
  sendNotification = async (message: NotificationMessage): Promise<string> => {
    const response = await Axios.post(
      this.uri(`admin/notifications/send`),
      message,
      {
        headers: await this.headers(),
      },
    );
    return response?.data?.id;
  };

  createSystemStatusMessage = async (messages: Messages): Promise<void> => {
    console.log(messages);
    await Axios.post(this.uri("systemstatus/messages"), messages, {
      headers: await this.headers(),
    });
  };
  deleteSystemStatusMesssage = async (
    platform: string,
    id: string,
  ): Promise<void> => {
    await Axios.delete(this.uri(`systemstatus/messages/${platform}/${id}`), {
      headers: await this.headers(),
    });
  };

  getRemoteConfigs = async (platform: string) => {
    const url = this.uri(`configs/${platform}`);
    const result = await Axios.get(url, {
      headers: await this.headers(),
    });
    return result.data?.config;
  };

  updateRemoteConfigs = async (platform: string, configObj: any) => {
    const result = await Axios.post(
      this.uri(`admin/configs/${platform}`),
      configObj,
      {
        headers: await this.headers(),
      },
    );
    return result.data?.message;
  };

  getAllStickerCollections = async (): Promise<StickerCollection[]> => {
    const result = await Axios.get(this.uri("admin/stickercollections"), {
      headers: await this.headers(),
    });
    return result.data;
  };

  createStickerCollection = async (request: any): Promise<void> => {
    await Axios.put<string>(this.uri("admin/stickercollection/new"), request, {
      headers: await this.headers(),
    });
  };

  updateStickerCollection = async (
    collectionId: number,
    request: any,
  ): Promise<void> => {
    await Axios.put<string>(
      this.uri(`admin/stickercollection/${collectionId}`),
      request,
      {
        headers: await this.headers(),
      },
    );
  };

  getStickerCOllectionById = async (
    collectionId: number,
  ): Promise<StickerCollection> => {
    const result = await Axios.get(
      this.uri(`admin/stickercollection/${collectionId}`),
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };

  getStickersForCollection = async (
    collectionId: number,
  ): Promise<Sticker[]> => {
    const result = await Axios.get(
      this.uri(`admin/stickersbycollection/${collectionId}`),
      {
        headers: await this.headers(),
      },
    );
    return result.data.stickers;
  };

  createSticker = async (request: StickerCreateRequest): Promise<void> => {
    await Axios.put<string>(this.uri("admin/sticker/new"), request, {
      headers: await this.headers(),
    });
  };

  updateSticker = async (stickerId: number, request: any): Promise<void> => {
    await Axios.put<string>(this.uri(`admin/sticker/${stickerId}`), request, {
      headers: await this.headers(),
    });
  };

  deleteStickerCollection = async (collectionId: number): Promise<void> => {
    await Axios.delete(this.uri(`admin/stickercollection/${collectionId}`), {
      headers: await this.headers(),
    });
  };

  deleteSticker = async (stickerId: number): Promise<void> => {
    await Axios.delete(this.uri(`admin/sticker/${stickerId}`), {
      headers: await this.headers(),
    });
  };

  getOrderList = async (
    payload: MailOutOrderListRequest,
  ): Promise<MailOutOrderListResponse> => {
    const url = `${config.mailoutApiHost}/admin/orders`;
    const resp = await Axios.post<MailOutOrder[] | MailOutOrderListApiResponse>(
      url,
      payload,
      {
        headers: await this.headers(),
      },
    );
    const responseData = resp.data;
    const resolvedOrders = Array.isArray(responseData)
      ? responseData
      : responseData?.orders ?? [];
    const pagination = Array.isArray(responseData)
      ? {
          total: resolvedOrders.length,
          page: payload.page,
          pageSize: payload.pageSize,
          totalPages: Math.max(
            1,
            Math.ceil(resolvedOrders.length / payload.pageSize),
          ),
        }
      : {
          total: responseData?.pagination?.total ?? resolvedOrders.length,
          page: responseData?.pagination?.page ?? payload.page,
          pageSize: responseData?.pagination?.pageSize ?? payload.pageSize,
          totalPages:
            responseData?.pagination?.totalPages ??
            Math.max(
              1,
              Math.ceil(
                (responseData?.pagination?.total ?? resolvedOrders.length) /
                  payload.pageSize,
              ),
            ),
        };
    return {
      orders: resolvedOrders,
      page: pagination.page,
      pageSize: pagination.pageSize,
      total: pagination.total,
      totalPages: pagination.totalPages,
      pagination,
    };
  };

  getMailOutOrderDetail = async (
    orderId: string,
  ): Promise<MailOutOrderDetail> => {
    const url = `${config.mailoutApiHost}/admin/orders/${orderId}`;
    const result = await Axios.get<MailOutOrderDetail>(url, {
      headers: await this.headers(),
    });
    return result.data;
  };

  reuploadMailOutOrder = async (orderId: string): Promise<void> => {
    return Axios.post(
      `${config.mailoutApiHost}/admin/reupload`,
      { orderId },
      { headers: await this.headers() },
    );
  };

  cancelMailOutOrder = async (
    orderId: string,
    reason?: string,
  ): Promise<void> => {
    const url = `${config.mailoutApiHost}/admin/orders/${orderId}`;
    const payload: { action: string; reason?: string } = { action: "cancel" };
    if (reason) {
      payload.reason = reason;
    }
    await Axios.put(url, payload, {
      headers: await this.headers(),
    });
  };

  resendMailOutReceipt = async (
    orderId: string,
    email: string,
  ): Promise<void> => {
    const url = `${config.mailoutApiHost}/admin/orders/${orderId}/resend-receipt`;
    await Axios.post(
      url,
      { email },
      {
        headers: await this.headers(),
      },
    );
  };

  getTagManagementTree = async (): Promise<ApiTagTree> => {
    const result = await Axios.get(
      this.uri("admin/tag-management/all-tag-categories"),
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };

  // TODO: confirm endpoint path with backend
  generateBacksidePreview = async (
    format: string,
    templateHtml: string,
    sampleData: object,
  ): Promise<Blob> => {
    const result = await Axios.post(
      this.uri(`admin/backside-template/preview`),
      { format, templateHtml, sampleData },
      { headers: await this.headers(), responseType: "blob" },
    );
    return result.data;
  };

  createTagCategory = async (request: CreateCategoryRequest): Promise<void> => {
    await Axios.post(this.uri("admin/tag-management/categories"), request, {
      headers: await this.headers(),
    });
  };

  updateTagCategory = async (
    categoryId: number,
    request: UpdateCategoryRequest,
  ): Promise<void> => {
    await Axios.put(
      this.uri(`admin/tag-management/categories/${categoryId}`),
      request,
      {
        headers: await this.headers(),
      },
    );
  };

  deleteTagCategory = async (categoryId: number): Promise<void> => {
    await Axios.delete(
      this.uri(`admin/tag-management/categories/${categoryId}`),
      {
        headers: await this.headers(),
      },
    );
  };

  getPriceLists = async (
    productCode: string,
  ): Promise<PriceManagementProductPricing[]> => {
    const url = `${config.mailoutApiHost}/admin/price/details/${productCode}`;
    const result = await Axios.get<PriceManagementProductPricing[]>(url, {
      headers: await this.headers(),
    });
    return result.data;
  };

  updatePriceList = async (
    payload: PriceManagementUpdatePriceListRequest,
  ): Promise<PriceManagementUpdatePriceListResponse> => {
    const url = `${config.mailoutApiHost}/admin/price/priceList/${payload.priceListId}`;
    const result = await Axios.put<PriceManagementUpdatePriceListResponse>(
      url,
      payload,
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };

  clonePriceList = async (
    priceListId: string,
  ): Promise<PriceManagementCloneResponse> => {
    const url = `${config.mailoutApiHost}/admin/price/priceList/clone/${priceListId}`;
    const result = await Axios.post<PriceManagementCloneResponse>(
      url,
      {},
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };

  deletePriceList = async (
    priceListId: string,
  ): Promise<PriceManagementDeleteResponse> => {
    const url = `${config.mailoutApiHost}/admin/price/priceList/${priceListId}`;
    const result = await Axios.delete<PriceManagementDeleteResponse>(url, {
      headers: await this.headers(),
    });
    return result.data;
  };

  updateVatPrice = async (
    payload: PriceManagementUpdateVatRequest,
  ): Promise<PriceManagementUpdateVatResponse> => {
    const { priceListId, ...body } = payload;
    const url = `${config.mailoutApiHost}/admin/price/details/VAT/${priceListId}`;
    const result = await Axios.put<PriceManagementUpdateVatResponse>(
      url,
      body,
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };

  updateSapPrice = async (
    payload: PriceManagementUpdateSapPriceRequest,
  ): Promise<PriceManagementUpdateSapPriceResponse> => {
    const { priceListId, ...body } = payload;
    const url = `${config.mailoutApiHost}/admin/price/details/SAP/${priceListId}`;
    const result = await Axios.put<PriceManagementUpdateSapPriceResponse>(
      url,
      body,
      {
        headers: await this.headers(),
      },
    );
    return result.data;
  };

  saveBacksideTemplate = async (
    format: string,
    templateHtml: string,
  ): Promise<void> => {
    await Axios.post(
      this.uri(`admin/backside-template/save`),
      { format, templateHtml },
      { headers: await this.headers() },
    );
  };

  // Superseded by v2 presigned-URL flow below; kept for reference.
  generatePdfPreviewV1 = async (
    format: string,
    htmlContent: string,
    templateData: object,
  ): Promise<string> => {
    const result = await Axios.post(
      this.uri(`admin/pdfpreview/${format}`),
      { html_content: htmlContent, template_data: templateData },
      { headers: await this.headers() },
    );
    return result.data.pdfPreviewURL;
  };
  getPdfPreviewUploadUrl = async (
    type: BacksideFormat,
  ): Promise<PdfPreviewUploadUrlResponse> => {
    const result = await Axios.post(
      this.uri(`admin/pdfpreview/v2/upload-url`),
      { type },
      { headers: await this.headers() },
    );
    return result.data;
  };
  uploadPdfPreviewTemplate = async (
    uploadUrl: string,
    html: string,
    contentType: string,
  ): Promise<void> => {
    await Axios.put(uploadUrl, html, {
      headers: { "Content-Type": contentType },
    });
  };
  generatePdfPreviewV2 = async (
    type: BacksideFormat,
    templateKey: string,
    templateData: object,
  ): Promise<string> => {
    const result = await Axios.post(
      this.uri(`admin/pdfpreview/v2/${type}`),
      { template_key: templateKey, template_data: templateData },
      { headers: await this.headers() },
    );
    return result.data.pdfPreviewURL;
  };
}
export default new RvApi();
